import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { CircularProgress, Dialog } from '@material-ui/core';
import styles from './Register.module.css';
import form from 'components/RegisterComponents/authForm.module.css';
import shellStyles from 'components/RegisterComponents/SplitShell/SplitShell.module.css';
import SplitShell from 'components/RegisterComponents/SplitShell/SplitShell';
import StepIndicator from 'components/RegisterComponents/StepIndicator/StepIndicator';
import ContactForm from 'components/RegisterComponents/ContactForm/ContactForm';
import BillingForm from 'components/RegisterComponents/BillingForm/BillingForm';
import AuthService from 'services/AuthService';

import { useUserApi } from '../../hooks/useUserApi';

const ERROR_KEYS = {
  'Email already registered': 'register.error.emailExists',
  'Username already registered': 'register.error.usernameExists'
};

const Register = () => {
  const { t } = useTranslation();
  // 0: account, 1: contact, 2: billing (only when wantShop)
  const [step, setStep] = useState(0);
  const [contactData, setContactData] = useState({
    contactCountry: 'Magyarország',
    contactCity: '',
    contactZipCode: ''
  });
  const [billingData, setBillingData] = useState({
    billingVendor: 'SZAMLAZZ_HU',
    billingCity: '',
    billingZipCode: ''
  });
  const [wantShop, setWantShop] = useState(false);
  const [isSure, setIsSure] = useState(false);
  const [newsAccept, setNewsAccept] = useState(false);
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [billingSubmitted, setBillingSubmitted] = useState(false);

  const [open, setOpen] = useState(false);
  const [showError, setShowError] = useState(false);

  const { postUser, userLoading, userError, redirectOnSuccess, subscribeNewsletter } = useUserApi();

  const resetData = () => {
    setContactData({ contactCountry: 'Magyarország' });
    setBillingData({ billingVendor: 'SZAMLAZZ_HU' });
    setStep(0);
    setIsSure(false);
    setWantShop(false);
  };

  const handleRedirect = () => {
    AuthService.doLogin();
  };
  // The error belongs to the last attempt, so it goes away once the user moves on.
  const changeStep = (nextStep) => {
    setShowError(false);
    setStep(nextStep);
  };
  const handleNext = () => changeStep(step + 1);
  const handleBack = () => {
    changeStep(step - 1);
    setContactSubmitted(false);
    setIsSure(false);
  };

  const handleRegister = async () => {
    if (isSure) {
      await postUser({ ...contactData, resetData });
      setIsSure(false);
    } else {
      await postUser({
        ...contactData,
        ...billingData,
        resetData
      });
      setIsSure(false);
    }
  };

  // A taken email or username can only be fixed on the account step.
  useEffect(() => {
    setShowError(Boolean(userError));
    if (ERROR_KEYS[userError]) {
      setStep(0);
    }
  }, [userError]);

  useEffect(() => {
    if (!wantShop) {
      if (contactData && contactSubmitted && isSure) {
        handleRegister();
        setContactSubmitted(false);
      }
    } else {
      // contactSubmitted stays set, so a failed attempt can be resubmitted from the billing step.
      if (contactData && billingData && billingSubmitted && contactSubmitted) {
        handleRegister();
        setBillingSubmitted(false);
      }
    }
  }, [contactData, billingData, billingSubmitted, contactSubmitted, wantShop, isSure]);

  const steps = [t('register.accountStep'), t('register.contactStep')];
  if (wantShop) {
    steps.push(t('register.billingStep'));
  }

  return (
    <SplitShell
      kicker={t('register.panel.kicker')}
      title={t('register.panel.title')}
      text={t('register.panel.text')}
      backHref='https://iskolaevkonyv.hu'
      backLabel={t('register.backToHome')}>
      {userLoading && (
        <div className={styles.loadingContainer} role='status' aria-label={t('common.loading')}>
          <CircularProgress size={56} color='inherit' />
        </div>
      )}
      {redirectOnSuccess ? (
        <div className={styles.success}>
          <div className={styles.successBadge}>
            <svg viewBox='0 0 32 32' fill='none' aria-hidden='true'>
              <path d='M7 17l6 6 12-14' stroke='currentColor' strokeWidth='3' strokeLinecap='round' />
            </svg>
          </div>
          <h1 className={styles.title}>{t('register.success.title')}</h1>
          <p className={styles.successMessage}>{t('register.success.message')}</p>
          <button type='button' className={`${form.button} ${form.buttonPrimary}`} onClick={handleRedirect}>
            {t('common.login')}
          </button>
        </div>
      ) : (
        <>
          <header className={styles.header}>
            <h1 className={styles.title}>{t('register.welcome')}</h1>
            <p className={styles.subtitle}>{t('register.subtitle')}</p>
          </header>
          <div className={styles.steps}>
            <StepIndicator steps={steps} active={step} />
          </div>
          {showError && (
            <div className={`${form.alert} ${styles.alert}`} role='alert'>
              <strong>{t('register.error.title')}</strong>
              {t(ERROR_KEYS[userError] || 'register.error.generalError')}
            </div>
          )}
          {step < 2 ? (
            <ContactForm
              contactData={contactData}
              setContactData={setContactData}
              step={step}
              setStep={changeStep}
              handleNext={handleNext}
              setOpen={setOpen}
              setSubmitted={setContactSubmitted}
              wantShop={wantShop}
              setWantShop={setWantShop}
              newsAccept={newsAccept}
              setNewsAccept={setNewsAccept}
              subscribeNewsletter={subscribeNewsletter}
            />
          ) : (
            <BillingForm
              setBillingData={setBillingData}
              billingData={billingData}
              contactData={contactData}
              handleBack={handleBack}
              setSubmitted={setBillingSubmitted}
              newsAccept={newsAccept}
              subscribeNewsletter={subscribeNewsletter}
            />
          )}
          <div className={styles.loginLinkHolder}>
            <button type='button' className={styles.loginLink} onClick={handleRedirect}>
              {t('register.haveAccount')}
            </button>
          </div>
        </>
      )}
      <Dialog
        open={open}
        onClose={() => {
          setOpen(false);
          setIsSure(false);
        }}
        aria-labelledby='register-confirm-title'
        PaperProps={{ className: `${shellStyles.theme} ${styles.dialogPaper}` }}>
        <h2 id='register-confirm-title' className={styles.dialogTitle}>
          {t('register.confirm.title')}
        </h2>
        <div className={form.actions}>
          <button
            type='button'
            className={`${form.button} ${form.buttonGhost}`}
            onClick={() => {
              setOpen(false);
              setIsSure(false);
            }}>
            {t('common.cancel')}
          </button>
          <button
            type='button'
            className={`${form.button} ${form.buttonPrimary}`}
            onClick={() => {
              setOpen(false);
              setIsSure(true);
            }}>
            {t('common.yes')}
          </button>
        </div>
      </Dialog>
    </SplitShell>
  );
};

export default Register;
